import { useState, useRef, useEffect } from "react";

import { Button } from "@mui/material";
import { Pause, PlayArrow } from "@mui/icons-material";
import styles from "./VideoTrimmer.module.css";

const VideoTimeline = ({ duration, trimSettings, onTrimChange, videoRef }) => {
  const [isDragging, setIsDragging] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const timelineRef = useRef(null);

  const formatTime = (seconds) => {
    // Ensure we have a valid number
    if (!isFinite(seconds) || isNaN(seconds)) {
      seconds = 0;
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getPositionFromTime = (time) => {
    // Validate inputs
    if (!isFinite(time) || !isFinite(duration) || duration <= 0) {
      return 0;
    }
    return (time / duration) * 100;
  };

  const getTimeFromPosition = (clientX) => {
    if (!timelineRef.current) return 0;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = (clientX - rect.left) / rect.width;
    const calculatedTime = position * duration;

    // Ensure we return a valid finite number
    if (!isFinite(calculatedTime) || isNaN(calculatedTime)) {
      return 0;
    }

    return Math.max(0, Math.min(duration, calculatedTime));
  };

  // Update current time from video and handle looping
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      const videoTime = video.currentTime;
      if (isFinite(videoTime) && !isNaN(videoTime)) {
        setCurrentTime(videoTime);

        // Loop back to start if we've reached the end trim point while playing
        if (
          videoTime >= trimSettings.endTime &&
          !video.paused &&
          isFinite(trimSettings.endTime)
        ) {
          const startTime = isFinite(trimSettings.startTime)
            ? trimSettings.startTime
            : 0;
          video.currentTime = startTime;
        }
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoRef, trimSettings.startTime, trimSettings.endTime]);

  const handleMouseDown = (type) => (e) => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newTime = getTimeFromPosition(e.clientX);

    if (isDragging === "start") {
      const newStart = Math.min(newTime, trimSettings.endTime - 0.1);
      if (isFinite(newStart) && isFinite(trimSettings.endTime)) {
        onTrimChange(newStart, trimSettings.endTime);
      }
    } else {
      const newEnd = Math.max(newTime, trimSettings.startTime + 0.1);
      if (isFinite(newEnd) && isFinite(trimSettings.startTime)) {
        onTrimChange(trimSettings.startTime, newEnd);
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, trimSettings]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      // Ensure we set a valid start time
      const startTime = isFinite(trimSettings.startTime)
        ? trimSettings.startTime
        : 0;
      console.log("Setting video currentTime to:", startTime);
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimelineClick = (e) => {
    if (isDragging) return;

    const clickTime = getTimeFromPosition(e.clientX);
    console.log("Timeline clicked, setting time to:", clickTime);

    if (videoRef.current && isFinite(clickTime)) {
      videoRef.current.currentTime = clickTime;
    }
  };

  // Validate trim settings to prevent rendering issues
  const validStartTime = isFinite(trimSettings.startTime)
    ? trimSettings.startTime
    : 0;
  const validEndTime = isFinite(trimSettings.endTime)
    ? trimSettings.endTime
    : duration;
  const validCurrentTime = isFinite(currentTime) ? currentTime : 0;
  const validDuration = isFinite(duration) ? duration : 0;

  return (
    <div className={`${styles.space_y_4}`}>
      <div className={`${styles.relative}`}>
        <div
          ref={timelineRef}
          className={`${styles.timeLineMain}`}
          onClick={handleTimelineClick}
        >
          {/* Timeline background */}
          <div
            className={`${styles.absolute} ${styles.inset_0} ${styles.bg_gradient_to_r} ${styles.from_slate_600} ${styles.to_slate_700}`}
          />

          {/* Selected region */}
          <div
            className={`${styles.selectedRegion}`}
            style={{
              left: `${getPositionFromTime(validStartTime)}%`,
              width: `${
                getPositionFromTime(validEndTime) -
                getPositionFromTime(validStartTime)
              }%`,
            }}
          />

          {/* Progress indicator */}
          <div
            className={`${styles.progressIndicator}`}
            style={{
              left: `${getPositionFromTime(validCurrentTime)}%`,
              transform: "translateX(-50%)",
            }}
          />

          {/* Start handle with improved visibility */}
          <div
            className={`${styles.handle}`}
            style={{ left: `${getPositionFromTime(validStartTime)}%` }}
            onMouseDown={handleMouseDown("start")}
          >
            <div />
          </div>

          {/* End handle with improved visibility */}
          <div
            className={`${styles.handle}`}
            style={{
              left: `${getPositionFromTime(validEndTime)}%`,
              transform: "translateX(-100%)",
            }}
            onMouseDown={handleMouseDown("end")}
          >
            <div />
          </div>

          {/* Time markers */}
          <div
            className={`${styles.absolute} ${styles.inset_0} ${styles.flex} ${styles.items_center} ${styles.justify_between} ${styles.px_2} ${styles.text_xs} ${styles.text_slate_300} ${styles.pointer_events_none} ${styles.z_10}`}
          >
            <span>{formatTime(0)}</span>
            <span>{formatTime(validDuration)}</span>
          </div>
        </div>

        {/* Time display */}
        <div className={`${styles.timeDisplay}`}>
          <div>Start: {formatTime(validStartTime)}</div>
          <div>
            <div
              className={`${styles.text_slate_300} ${styles.text_sm}`}
              style={{ marginBottom: "4px" }}
            >
              {formatTime(validCurrentTime)} / {formatTime(validDuration)}
            </div>
            <Button onClick={handlePlayPause} size="sm" variant="contained">
              {isPlaying ? (
                <Pause fontSize="small" sx={{ color: "white" }} />
              ) : (
                <PlayArrow fontSize="small" sx={{ color: "white" }} />
              )}
            </Button>
          </div>

          <div>End: {formatTime(validEndTime)}</div>
        </div>
      </div>
    </div>
  );
};

export default VideoTimeline;
